import { Guide } from '../../types/guide';

const guide: Guide = {
  slug: 'text-editor-tips-and-shortcuts',
  title: 'Online Text Editor: Tips, Tricks and Keyboard Shortcuts',
  description:
    'Get the most from the free online text editor: keyboard shortcuts, selection-aware tools, syntax highlighting, preview modes, opening files and autosave.',
  category: 'Text editor',
  publishedDate: '2026-11-03',
  affiliateCategory: 'writing',
  body: `
The [Text Editor](/text-editor) looks simple, but it's packed with time-savers. Here are the ones worth learning.

## Keyboard shortcuts

| Shortcut | Action |
|---|---|
| **Ctrl+Z** / **⌘Z** | Undo (typing is grouped; every tool and paste is its own step) |
| **Ctrl+Y**, **Ctrl+Shift+Z** | Redo |
| **Ctrl+F** or **Ctrl+H** | Find & replace |
| **Enter** / **Shift+Enter** (in Find) | Next / previous match |
| **Esc** (in Find) | Close find & replace |
| **Ctrl+S** / **⌘S** | Download in the current format |

## Tools follow your selection

Every text tool and format conversion works on the **selection** if you have one, and on the whole document if you don't. The chip above the tools tells you which: **Applies to selection** or **Applies to all text**. Select one paragraph and click *Title Case*, or one table and convert it to JSON, without touching anything else.

## The tool groups at a glance

- **Fix line breaks & whitespace** — *Fix PDF line breaks*, trim lines, collapse spaces and blank lines, tabs ↔ spaces, normalize line endings.
- **Change case** — UPPERCASE, lowercase, Title Case, Sentence case, camelCase, PascalCase, snake_case, CONSTANT_CASE, kebab-case.
- **Sort & edit lines** — natural sort (so *item2* comes before *item10*), sort by length, reverse, shuffle, remove duplicates or empty lines, number lines, join lines, one sentence per line.
- **Add, filter & wrap lines** — add a prefix or suffix to every line, keep or remove lines containing a word, hard-wrap at a set width.
- **Clean up** — strip HTML or Markdown, straighten smart quotes, remove accents, emoji, punctuation, numbers, URLs or invisible characters.
- **Encode, decode & format** — Base64, URL encoding, HTML entities, JSON string escaping, ROT13, and formatting or minifying JSON and XML.

## Syntax highlighting

The **<> Auto** button shows what the editor detected — Markdown, HTML, JSON, CSV (each column gets its own colour), or one of ~18 programming languages such as JavaScript, Python, SQL or Go. Ordinary writing is left uncoloured. If the guess is wrong, pick a language from the menu, or choose **Off**. Highlighting pauses automatically on very large documents to keep typing fast.

## See the result: Edit, Split and Preview

- **Split** shows the editor and a live preview side by side.
- **Preview** renders Markdown, shows HTML safely (scripts can't run), shows CSV/TSV as a table and pretty-prints JSON.

## Make it comfortable

- **Word wrap** on or off (the wrap icon). With wrap off, **line numbers** appear in the gutter.
- **A− / A+** change the font size.
- Your view, wrap, font size and highlighting choice are remembered.

## Opening and saving

- **Open** text files (\`.txt\`, \`.md\`, \`.html\`, \`.csv\`, \`.tsv\`, \`.json\`, \`.xml\`, \`.yaml\`, \`.log\`) or **drag and drop** them onto the editor.
- You can even open **PDF, Word and Excel** files — they're converted to Markdown first.
- **Download** in any supported format, as a formatted [PDF](/guides/export-text-and-markdown-to-pdf), or turn CSV data into an **Excel workbook (.xlsx)**.
- Your draft **autosaves in this browser**, so a refresh or accidental tab close loses nothing. **Clear** empties the editor, and Undo brings it back.

## The status bar

Along the bottom you'll see words, characters, lines, reading time, the cursor's line and column, how many characters are selected, and the detected format — useful for length limits and for spotting the format the converter will use.

## More guides

- [Clean up text copied from a PDF](/guides/clean-up-text-copied-from-pdf)
- [Convert between CSV, JSON, XML, YAML, Markdown and HTML](/guides/convert-csv-json-xml-yaml-markdown-html)
- [Find & replace regex cheat sheet](/guides/find-and-replace-regex-cheat-sheet)
`,
};

export default guide;
